import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { isApiError } from '../../api/client';
import { useSession } from '../../app/session';
import { toast } from '../../app/toast';
import { PageHeader } from '../../components/PageHeader';
import { useT, type DictKey } from '../../i18n';
import { describeError } from '../../lib/errors';

/** Forced after a login with `must_change_password`, and reachable from Settings. */
export function ChangePasswordPage({ forced = false }: { forced?: boolean }) {
  const t = useT();
  const nav = useNavigate();
  const change = useSession((s) => s.changePassword);
  const logout = useSession((s) => s.logout);
  const [cur, setCur] = useState('');
  const [next, setNext] = useState('');
  const [rep, setRep] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<DictKey | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (next.length < 10) return setError('pwd.short');
    if (next !== rep) return setError('pwd.mismatch');
    setBusy(true);
    setError(null);
    try {
      await change(cur, next);
      toast(t('pwd.done'));
      nav('/', { replace: true });
    } catch (err) {
      setError(isApiError(err) && err.code === 'wrong_password' ? 'pwd.wrong' : describeError(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className={forced ? 'page min-h-[100dvh] pb-10 pt-[max(8px,env(safe-area-inset-top))]' : ''}>
      <PageHeader title={t(forced ? 'pwd.title' : 'settings.password')} back={forced ? undefined : '/more'} />
      <p className="text-[15px] text-txt">{t('pwd.intro')}</p>
      <form className="mt-5 space-y-4" onSubmit={submit} noValidate>
        <div>
          <label htmlFor="pw-cur" className="label">
            {t('pwd.current')}
          </label>
          <input id="pw-cur" type="password" className="input" autoComplete="current-password" value={cur} onChange={(e) => setCur(e.target.value)} />
        </div>
        <div>
          <label htmlFor="pw-new" className="label">
            {t('pwd.new')}
          </label>
          <input id="pw-new" type="password" className="input" autoComplete="new-password" value={next} onChange={(e) => setNext(e.target.value)} />
          <p className="mt-1 text-[14px] text-lab">{t('pwd.short')}</p>
        </div>
        <div>
          <label htmlFor="pw-rep" className="label">
            {t('pwd.repeat')}
          </label>
          <input id="pw-rep" type="password" className="input" autoComplete="new-password" value={rep} onChange={(e) => setRep(e.target.value)} />
        </div>
        {error ? (
          <p role="alert" className="text-bad">
            {t(error)}
          </p>
        ) : null}
        <button type="submit" className="btn-primary min-h-big w-full text-[18px]" disabled={busy}>
          {t('pwd.submit')}
        </button>
        {forced ? (
          <button type="button" className="btn w-full" onClick={() => void logout().then(() => nav('/login', { replace: true }))}>
            {t('settings.logout')}
          </button>
        ) : null}
      </form>
    </div>
  );
}
